import {
  FocusContext,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation";
import React, { useCallback, useLayoutEffect, useMemo } from "react";
import _ from "lodash";
import { ShowCaseComponentSwitch } from "../showcase/showcase.utils";
import smoothScroll from "../../utils/animateScroll";
import { getVerticalFocusScrollTarget } from "../../utils/focusScroll";
import { useHomeShowcaseData } from "../../utils/hooks/useHomeShowcaseData";
import { useInputMode } from "../../utils/hooks/useInputMode";
import { resetHomeViewport } from "./focusHelpers";

const HomeContent = ({ showCaseData, isShowcasePage = false, contentKind }) => {
  const { ref, focusKey } = useFocusable({
    focusKey: "home_showcase",
    isFocusBoundary: true,
    focusBoundaryDirections: ["up", "down", "left"],
  });

  // Fetch private/personalized showcase data - only for home page, not showcase pages
  const homeShowcaseData = useHomeShowcaseData();
  // Skip private data on showcase pages (movie, series, music, etc.)
  const continueWatch = isShowcasePage || !homeShowcaseData.isLoggedIn ? [] : homeShowcaseData.continueWatch;
  const youMayLike = isShowcasePage || !homeShowcaseData.isLoggedIn ? [] : homeShowcaseData.youMayLike;
  const recentlyUpdated = isShowcasePage ? [] : homeShowcaseData.recentlyUpdated;

  const { isMouseFocusRef } = useInputMode();
  useLayoutEffect(() => {
    if (!ref.current) return;
    var timers;

    resetHomeViewport(ref.current);
    timers = [0, 80, 240].map(function (delay) {
      return setTimeout(function () {
        resetHomeViewport(ref.current);
      }, delay);
    });

    return function () {
      timers.forEach(function (timer) {
        clearTimeout(timer);
      });
    };
  }, [ref, showCaseData]);

  const onRowFocus = useCallback(
    (props) => {
      if (!ref.current) return;
      if (isMouseFocusRef.current) return;

      const targetScroll = getVerticalFocusScrollTarget(ref.current, props || {}, {
        topPadding: 84,
        bottomPadding: 112,
        align: "center",
      });
      smoothScroll(ref, targetScroll, 500);
    },
    [ref, isMouseFocusRef]
  );

  const homeData = useMemo(() => {
    let results = [];

    Object.keys(showCaseData)?.map((key) =>
      showCaseData[key]?.map((item) => results.push(item))
    );

    // Sort by priority
    results = _.sortBy(results, (x) => x.priority);

    // Create final array with injected rows at specific positions
    // (matching Flutter TV: Continue Watch after 2nd, You May Like after 5th, Recently Updated at end)
    const finalResults = [];
    let processedCount = 0;

    for (let i = 0; i < results.length; i++) {
      finalResults.push(results[i]);
      processedCount++;

      // After 2nd item: Insert Continue Watch (if available)
      if (processedCount === 2 && continueWatch && continueWatch.length > 0) {
        finalResults.push({
          _id: "recently_updated_row",
          type: "recentlyUpdated",
          title: "به‌روز شده‌ها",
          items: recentlyUpdated,
          priority: 9999,
        });
        finalResults.push({
          _id: "continue_watch_row",
          type: "continueWatch",
          title: "ادامه تماشا",
          items: continueWatch,
          priority: -1, // Will be ignored since we're placing manually
        });
      }

      // After 5th item: Insert You May Like (if available)
      if (processedCount === 5 && youMayLike && youMayLike.length > 0) {
        finalResults.push({
          _id: "you_may_like_row",
          type: "youMayLike",
          title: "شاید دوست داشته باشید",
          items: youMayLike,
          priority: -1,
        });
      }
    }

    return finalResults;
  }, [showCaseData, continueWatch, youMayLike, recentlyUpdated]);

  return (
    <FocusContext.Provider value={focusKey}>
      <div className="legacy-route-content legacy-route-content-plain">
        <div className="legacy-route-scroll legacy-home-route-scroll" ref={ref}>
          <div>
            {homeData?.map((item, index) => (
              <ShowCaseComponentSwitch
                key={"sc_" + index + '_' + item._id}
                item={item}
                onRowFocus={onRowFocus}
                parentIndex={index}
                contentKind={contentKind}
              />
            ))}
          </div>
        </div>
      </div>
    </FocusContext.Provider>
  );
};

export default HomeContent;
